import { AssetRef, Shot, ShotVersion } from '../../../types';
import { IneligibleReason } from '../constants';
import { tosAccelerate } from '../../../utils/tosAccelerate';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface VideoShotEntry {
  shot: Shot;
  videoURL: string;
  ineligibleReason: IneligibleReason | null;
  hoursRemaining: number | null;
}

const MAX_VIDEO_REF_DURATION = 15;
const ARK_URL_MAX_AGE_MS = 23 * 60 * 60 * 1000;

// 可引用视频列表：排除当前分镜，仅收 completed+videoURL；Ark 平台为不可用项标记
// ineligibleReason 但仍保留在列表中（tab 计数与面板共用本函数，口径一致）
export function listVideoShotEntries({
  shots,
  shotVersions,
  selectedVideoVersions,
  currentShotId,
  videoPlatform,
  now = Date.now(),
}: {
  shots: Shot[];
  shotVersions: Record<string, ShotVersion[]>;
  selectedVideoVersions: Record<string, string>;
  currentShotId: string | null;
  videoPlatform?: string;
  now?: number;
}): VideoShotEntry[] {
  const isArkPlatform = videoPlatform === 'volcano-ark';
  return shots
    .filter(shot => shot.id !== currentShotId)
    .map((shot): VideoShotEntry | null => {
      const versions = shotVersions[shot.id] || [];
      const selectedVerId = selectedVideoVersions[shot.id] || shot.selectedVersionId;
      let videoVersion = selectedVerId ? versions.find(version => version.id === selectedVerId && version.taskStatus === 'completed' && version.videoURL) : undefined;
      if (!videoVersion) videoVersion = [...versions].reverse().find(version => version.taskStatus === 'completed' && version.videoURL);
      if (!videoVersion) return null;

      let ineligibleReason: IneligibleReason | null = null;
      let hoursRemaining: number | null = null;
      if (isArkPlatform) {
        if (videoVersion.videoPlatform !== 'volcano-ark') {
          ineligibleReason = 'cross-platform';
        } else if (!videoVersion.completedAt) {
          ineligibleReason = 'no-completed-at';
        } else {
          const age = now - new Date(videoVersion.completedAt).getTime();
          if (age > ARK_URL_MAX_AGE_MS) {
            ineligibleReason = 'expired';
          } else {
            hoursRemaining = (ARK_URL_MAX_AGE_MS - age) / (60 * 60 * 1000);
          }
        }
      }

      return { shot, videoURL: videoVersion.videoURL!, ineligibleReason, hoursRemaining };
    })
    .filter((entry): entry is VideoShotEntry => !!entry);
}

interface ShotVideoRefPanelProps {
  currentShotId: string | null;
  addingVideoRef: boolean;
  shots: Shot[];
  shotVersions: Record<string, ShotVersion[]>;
  selectedVideoVersions: Record<string, string>;
  videoPlatform?: string;
  t: Translate;
  getAssetRefs: (shot: Shot) => AssetRef[];
  ineligibleLabel: (reason: IneligibleReason) => string;
  ineligibleTitle: (reason: IneligibleReason) => string;
  onAddVideoRef: (shotId: string, sourceShotIndex: number, videoURL: string) => void;
}

export default function ShotVideoRefPanel({
  currentShotId,
  addingVideoRef,
  shots,
  shotVersions,
  selectedVideoVersions,
  videoPlatform,
  t,
  getAssetRefs,
  ineligibleLabel,
  ineligibleTitle,
  onAddVideoRef,
}: ShotVideoRefPanelProps) {
  const currentShot = currentShotId ? shots.find(shot => shot.id === currentShotId) : null;
  const currentRefs = currentShot ? getAssetRefs(currentShot) : [];
  const alreadyAddedUrls = new Set(currentRefs.filter(ref => ref.source === 'shot_ref').map(ref => ref.imageUrl));

  const urlToDuration: Record<string, number> = {};
  for (const shot of shots) {
    const versions = shotVersions[shot.id] || [];
    for (const version of versions) {
      if (version.taskStatus === 'completed' && version.videoURL) {
        urlToDuration[version.videoURL] = shot.duration;
      }
    }
  }

  const currentRefDuration = currentRefs
    .filter(ref => ref.source === 'shot_ref')
    .reduce((sum, ref) => sum + (urlToDuration[ref.imageUrl] || 0), 0);

  const isArkPlatform = videoPlatform === 'volcano-ark';
  const videoShots = listVideoShotEntries({ shots, shotVersions, selectedVideoVersions, currentShotId, videoPlatform });

  if (videoShots.length === 0) {
    return <p style={{ color: 'var(--text-3)', fontSize: '0.85rem', margin: 0 }}>{t('videoPickerModal.noVideos')}</p>;
  }

  return (
    <>
      {isArkPlatform && (
        <div style={{
          fontSize: '0.75rem',
          color: 'var(--text-2)',
          background: 'rgba(59,130,246,0.08)',
          border: '1px solid rgba(59,130,246,0.2)',
          borderRadius: 'var(--radius-sm, 4px)',
          padding: '6px 10px',
          marginBottom: '0.5rem',
          lineHeight: 1.5,
        }}>
          {t('videoPickerModal.arkLimitHtml')}
        </div>
      )}
      <div style={{ fontSize: '0.8rem', color: currentRefDuration >= MAX_VIDEO_REF_DURATION ? 'var(--error)' : 'var(--text-2)', marginBottom: '0.5rem' }}>
        {t('videoPickerModal.refDuration', { current: currentRefDuration, max: MAX_VIDEO_REF_DURATION })}
      </div>
      <div style={{ overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem', maxHeight: '75vh', paddingRight: '10px' }}>
        {videoShots.map(({ shot, videoURL, ineligibleReason, hoursRemaining }) => {
          const added = alreadyAddedUrls.has(videoURL);
          const wouldExceed = !added && currentRefDuration + shot.duration > MAX_VIDEO_REF_DURATION;
          const ineligible = !!ineligibleReason;
          const disabled = added || wouldExceed || ineligible || addingVideoRef;
          const tooltip = ineligibleReason
            ? ineligibleTitle(ineligibleReason)
            : wouldExceed
              ? t('videoPickerModal.overtimeTitle', { total: currentRefDuration + shot.duration, max: MAX_VIDEO_REF_DURATION })
              : '';
          const lowTime = hoursRemaining !== null && hoursRemaining < 3;
          return (
            <div
              key={shot.id}
              onClick={() => !disabled && currentShotId && onAddVideoRef(currentShotId, shot.index, videoURL)}
              style={{ width: '100%', border: added ? '2px solid var(--theme-storyboard)' : 'none', borderRadius: 'var(--radius)', overflow: 'hidden', cursor: disabled ? 'not-allowed' : 'pointer', background: 'var(--surface, #fff)', boxShadow: added ? 'none' : 'var(--shadow-xs, 0 1px 6px rgba(0,0,0,0.05))', opacity: disabled && !added ? 0.5 : 1, transition: 'box-shadow var(--transition-fast)' }}
              title={tooltip}
              onMouseEnter={(event) => { if (!disabled) event.currentTarget.style.boxShadow = 'var(--shadow)'; }}
              onMouseLeave={(event) => { event.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ position: 'relative' }}>
                <video src={tosAccelerate(videoURL)} preload="metadata" style={{ width: '100%', display: 'block', filter: ineligible ? 'grayscale(1)' : 'none' }} />
                <span style={{ position: 'absolute', top: '4px', left: '4px', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '0.7rem', borderRadius: '3px', padding: '1px 6px' }}>
                  {t('videoPickerModal.shotLabel', { index: String(shot.index).padStart(2, '0') })}
                </span>
                {added && (
                  <span style={{ position: 'absolute', bottom: '4px', right: '4px', background: 'var(--primary)', color: '#fff', fontSize: '0.6rem', borderRadius: '3px', padding: '1px 5px' }}>{t('videoPickerModal.added')}</span>
                )}
                {!added && ineligibleReason && (
                  <span style={{ position: 'absolute', bottom: '4px', right: '4px', background: 'rgba(107,114,128,0.9)', color: '#fff', fontSize: '0.6rem', borderRadius: '3px', padding: '1px 5px' }}>
                    {ineligibleLabel(ineligibleReason)}
                  </span>
                )}
                {!added && !ineligible && wouldExceed && (
                  <span style={{ position: 'absolute', bottom: '4px', right: '4px', background: 'var(--error, #ef4444)', color: '#fff', fontSize: '0.6rem', borderRadius: '3px', padding: '1px 5px' }}>{t('videoPickerModal.overtime')}</span>
                )}
              </div>
              <div style={{ padding: '4px 6px' }}>
                <div style={{ fontSize: '0.7rem', color: wouldExceed ? 'var(--error, #ef4444)' : 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {shot.duration}{t('preview.durationSuffix')}
                  {hoursRemaining !== null && (
                    <span style={{ marginLeft: '4px', color: lowTime ? 'var(--warning, #e6a23c)' : 'var(--text-3)' }}>
                      · {t('videoPickerModal.remainingHours', { time: hoursRemaining >= 1 ? `${Math.floor(hoursRemaining)}h` : `${Math.max(1, Math.round(hoursRemaining * 60))}m` })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
